import { DateFormat, calculateTimeDifferenceBetweenDates, formatDateTimeWithLocale } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { CommonMessage, useLocale } from '@frontend/lang';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { ExtendedPudoTransaction } from '@frontend/transaction/types';
import { LuArrowRight, LuCalendar, LuDoorClosed, LuUser } from 'react-icons/lu';

import { useKioskNavigate } from '../utils/kiosk-navigation';

const formatTrackingNumber = (id: string): string => {
    if (id.length <= 12) return id;
    return `${id.substring(0, 8)}...${id.substring(id.length - 4)}`;
};

export interface PudoTransactionItemProps {
    transaction: ExtendedPudoTransaction;
    isLast: boolean;
    badgeType: 'state' | 'awaiting-drop-off' | 'awaiting-pick-up';
}

export const PudoTransactionItemComponent = (props: PudoTransactionItemProps) => {
    const navigate = useKioskNavigate();
    const { locale } = useLocale();

    const renderBadge = () => {
        if (props.badgeType === 'awaiting-drop-off') {
            const timeDifference = calculateTimeDifferenceBetweenDates(props.transaction.update_timestamp, new Date(Date.now()).toISOString());
            return (
                <DefaultBadge
                    label={
                        <>
                            {CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR} {timeDifference}
                        </>
                    }
                    styles={{ container: { color: 'bg-status-warning' } }}
                />
            );
        }
        if (props.badgeType === 'awaiting-pick-up') {
            if (!props.transaction.dropped_off_timestamp) {
                return null;
            }

            const timeDifference = calculateTimeDifferenceBetweenDates(props.transaction.dropped_off_timestamp, new Date(Date.now()).toISOString());
            return (
                <DefaultBadge
                    label={
                        <>
                            {CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR} {timeDifference}
                        </>
                    }
                    styles={{ container: { color: 'bg-status-info' } }}
                />
            );
        }
        return <TransactionStateBadge state={props.transaction.state} />;
    };

    return (
        <div
            className={`border-border-default bg-background-surface flex w-full cursor-pointer flex-col items-start justify-start gap-1.5 rounded-xl border p-4 py-3 font-medium`}
            onClick={() => navigate(`/spots/layout/pudo/transactions/${props.transaction.id}`)}>
            <div className='flex w-full items-center justify-between gap-2'>
                <p className='text-foreground-default leading-snug font-semibold'>{formatTrackingNumber(props.transaction.tracking_number ?? '')}</p>
                {renderBadge()}
            </div>
            <div className='text-brand-muted flex w-full flex-col gap-1'>
                <div className='flex items-center gap-2'>
                    <LuUser className='size-5 shrink-0' />
                    <p className='min-w-0 flex-1'>
                        {CommonMessage.OBJECTS.TRANSACTION.SENDER}: {props.transaction.senders?.[0]?.name || CommonMessage.OBJECTS.UNKNOWN.DEFAULT}
                    </p>
                    {(props.transaction.senders?.length ?? 0) > 1 && (
                        <div className='text-brand-muted shrink-0 self-stretch text-center text-sm font-medium'>+{props.transaction.senders!.length - 1}</div>
                    )}
                </div>

                <div className='flex items-center gap-2'>
                    <LuUser className='size-5 shrink-0' />
                    <p className='min-w-0 flex-1'>
                        {CommonMessage.OBJECTS.TRANSACTION.RECEIVER}: {props.transaction.receivers?.[0]?.name || CommonMessage.OBJECTS.UNKNOWN.DEFAULT}
                    </p>
                    {(props.transaction.receivers?.length ?? 0) > 1 && (
                        <div className='text-brand-muted shrink-0 self-stretch text-center text-sm font-medium'>+{props.transaction.receivers!.length - 1}</div>
                    )}
                </div>
                <div className='flex items-center gap-2'>
                    <LuDoorClosed className='size-5 shrink-0' />
                    <p>
                        {props.transaction.slot
                            ? `SLOT ${props.transaction.slot.name}`
                            : CommonMessage.ERRORS.UNKNOWN_ENTITY(CommonMessage.OBJECTS.SLOT.DEFAULT)}
                    </p>
                </div>
                <div className='flex w-full items-center gap-2'>
                    <LuCalendar className='size-5 shrink-0' />
                    <p className='min-w-0 flex-1'>
                        {CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT}{' '}
                        {formatDateTimeWithLocale(new Date(props.transaction.creation_timestamp), locale, DateFormat.DD_MM_YYYY_HH_MM)}
                    </p>
                    <LuArrowRight
                        className='text-brand-primary size-5 shrink-0'
                        aria-hidden
                    />
                </div>
            </div>
        </div>
    );
};

export const PudoTransactionItem = (transaction: ExtendedPudoTransaction, index: number, isLast: boolean) => {
    return (
        <PudoTransactionItemComponent
            key={transaction.id}
            transaction={transaction}
            isLast={isLast}
            badgeType='state'
        />
    );
};
